import { Controller, useForm } from 'react-hook-form';
import { useToggle, useUpdateEffect } from 'react-use';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { parseAbsolute } from '@internationalized/date';
import { addHours, addMinutes } from 'date-fns';
import { MdOutlineClear, MdOutlineDoneAll, MdOutlineEdit } from 'react-icons/md';
import { Button, Input, Spinner, TmdxDatepicker } from '@ds';
import { ReservationDateTimeForm } from '@lp/features/Scheduler/components/ReservationCell/types';
import { getNumberInputValue, getNumberInputValueHandler } from '@lp/features/Scheduler/utils';
import { SOrganGroup } from '@lp/features/Scheduler/SchedulerSurgeons/types';
import { ReservationNode, ReservationType } from '@/api/apis/resources';
import { reservationTypeNames } from '@/constants/resources.constants';
import {
  useModifyCSRestPeriodMutation,
  useModifyRSHtLuRestPeriodMutation,
  useModifyRSLiRestPeriodMutation,
} from '@/queries/resource';
import { formatDate, getUserTimezone } from '@/utils/helpers';
import { DEFAULT_DATE_FORMAT_STRING } from '@/constants/date.constants';
import { CaseResourceType } from '@/api/apis/case';
import { getDefaultReservationDateTimeValues, reservationBarFormSchema } from '../config';

type ReservationBarTooltipProps = {
  isRestReadOnly: boolean;
  data: ReservationNode;
  resourceType: CaseResourceType;
  organGroup?: SOrganGroup;
};

export const ReservationBarTooltip = ({
  data,
  resourceType,
  organGroup,
  isRestReadOnly,
}: ReservationBarTooltipProps) => {
  const [isEdit, toggleIsEdit] = useToggle(false);
  const { mutate: mutateCSRestPeriod, isPending: isCSRestPeriodPending } = useModifyCSRestPeriodMutation();
  const { mutate: mutateRSHtLuRestPeriod, isPending: isRSHtLuRestPeriodPending } = useModifyRSHtLuRestPeriodMutation();
  const { mutate: mutateRSLiRestPeriod, isPending: isRSLiRestPeriodPending } = useModifyRSLiRestPeriodMutation();
  const { control, handleSubmit, reset, formState, setValue, watch } = useForm<ReservationDateTimeForm>({
    mode: 'onChange',
    resolver: standardSchemaResolver(reservationBarFormSchema),
    defaultValues: getDefaultReservationDateTimeValues(data),
  });
  const timezone = getUserTimezone();
  const [fromValue, durationValue, durationMinutesValue] = watch(['from', 'duration', 'durationMinutes']);

  useUpdateEffect(() => {
    reset(getDefaultReservationDateTimeValues(data));
  }, [data]);

  useUpdateEffect(() => {
    if (!fromValue) {
      setValue('to', '');
      return;
    }

    const fromWithHours = addHours(fromValue, durationValue ?? 0);
    const toValue = addMinutes(fromWithHours, durationMinutesValue ?? 0);

    setValue('to', toValue.toISOString());
  }, [fromValue, durationValue, durationMinutesValue]);

  const onSubmit = (formData: ReservationDateTimeForm) => {
    const updateBody = {
      employeeRestPeriodId: data.nodeId,
      restStartTime: formData.from,
      restDurationInHours: formData.duration,
      restDurationMinutes: formData.durationMinutes,
    };

    if (resourceType === CaseResourceType.ClinicalSpecialist) {
      mutateCSRestPeriod(updateBody);
    }

    if (resourceType === CaseResourceType.Surgeon && organGroup) {
      const mutateFunc = organGroup === SOrganGroup.Liver ? mutateRSLiRestPeriod : mutateRSHtLuRestPeriod;

      mutateFunc(updateBody);
    }
  };

  const handleEdit = () => {
    if (isEdit) reset();
    toggleIsEdit();
  };

  const isPending = isCSRestPeriodPending || isRSHtLuRestPeriodPending || isRSLiRestPeriodPending;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="relative flex flex-col gap-2 p-1.5">
      {isPending && (
        <div className="z-10 absolute w-full h-full flex justify-center items-center bg-white/70">
          <Spinner />
        </div>
      )}
      <div className="flex justify-between items-center gap-2 h-6">
        <h4 className="text-base">{reservationTypeNames[data.type]}</h4>
        {!isRestReadOnly && data.type === ReservationType.OnRest && (
          <Button
            size="sm"
            variant="light"
            disableRipple
            isDisabled={isPending}
            startContent={isEdit ? <MdOutlineClear size={18} /> : <MdOutlineEdit size={18} />}
            className="max-h-6 px-3"
            onPress={handleEdit}
          >
            {isEdit ? 'Cancel' : 'Edit'}
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Controller
          name="from"
          control={control}
          render={({ field: { value, onChange }, fieldState: { invalid } }) => (
            <div className="flex items-center gap-1">
              <span className="text-base-30 min-w-[72px]">Start Date: </span>
              {isEdit ? (
                <TmdxDatepicker
                  size="sm"
                  hourCycle={24}
                  aria-label="Start Date"
                  isInvalid={invalid}
                  value={value ? parseAbsolute(value, timezone) : null}
                  onChange={(dv) => onChange(dv ? dv.toDate(timezone).toISOString() : null)}
                />
              ) : (
                <div>{formatDate(value, DEFAULT_DATE_FORMAT_STRING)}</div>
              )}
            </div>
          )}
        />
        {isEdit && (
          <div className="flex items-center gap-1">
            <span className="text-base-30 min-w-[72px]">Duration: </span>
            <Controller
              name="duration"
              control={control}
              render={({ field: { value, onChange }, fieldState: { invalid } }) => (
                <div className="flex items-center gap-1">
                  <Input
                    size="sm"
                    type="number"
                    min={0}
                    isInvalid={invalid}
                    value={getNumberInputValue(value)}
                    onValueChange={getNumberInputValueHandler(onChange)}
                    className="max-w-16"
                  />
                  <span className="text-base text-m-r ms-1">hours</span>
                </div>
              )}
            />
            <Controller
              name="durationMinutes"
              control={control}
              render={({ field: { value, onChange }, fieldState: { invalid } }) => (
                <div className="flex items-center gap-1">
                  <Input
                    size="sm"
                    type="number"
                    min={0}
                    max={59}
                    isInvalid={invalid}
                    value={getNumberInputValue(value)}
                    onValueChange={getNumberInputValueHandler(onChange)}
                    className="max-w-16"
                  />
                  <span className="text-base text-m-r ms-1">minutes</span>
                </div>
              )}
            />
          </div>
        )}
        <Controller
          name="to"
          control={control}
          render={({ field: { value } }) => (
            <div className="flex items-center gap-1">
              <span className="text-base-30 min-w-[72px]">End Date: </span>
              <div>{formatDate(isEdit ? value : data.to, DEFAULT_DATE_FORMAT_STRING)}</div>
            </div>
          )}
        />
      </div>

      {isEdit && (
        <div className="flex justify-end h-6">
          {data.type === ReservationType.OnRest && (
            <Button
              size="sm"
              variant="light"
              type="submit"
              isDisabled={!formState.isValid || isPending}
              startContent={<MdOutlineDoneAll size={18} />}
              className="max-h-6 px-3"
            >
              Save
            </Button>
          )}
        </div>
      )}
    </form>
  );
};
